import { FC, ReactNode, useState } from 'react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from 'primereact/button';

interface Slide {
  heading: ReactNode;
  body: ReactNode;
  lottiePath: string;
  classNames: string;
}

const SLIDES: Slide[] = [
  {
    heading: <>Here&apos;s how it works.</>,
    body: (
      <>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0 tw-mb-2">
          Every student starts with a <span className="tw-font-bold">short diagnostic</span>.
        </p>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0 tw-mb-2">
          It asks harder or easier questions based on how the student is doing, so by the end, we have a clear picture
          of their starting point.
        </p>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0">
          This sets the baseline for everything that follows.
        </p>
      </>
    ),
    lottiePath: '/teacher-welcome-1-animation.json',
    classNames: 'paper-grid-bg-teacher-welcome-purple tw-bg-[#eef0ff] ',
  },
  {
    heading: <>We translate those results into a Roadmap.</>,
    body: (
      <>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0 tw-mb-2">
          Each student gets their own Roadmap — a personalized pathway aligned to your curriculum and standards that
          will address any unfinished learning from previous grades.
        </p>
        <p className="tw-font-atkinson lg:tw-text-xl tw-text-lg tw-font-bold tw-m-0">
          If a student already knows something, we’ll skip it, shortening their path and focusing time on what they
          actually need to learn.
        </p>
      </>
    ),
    lottiePath: '/teacher-welcome-2-animation.json',
    classNames: 'paper-grid-bg-teacher-welcome-purple tw-bg-[#fefbf9]',
  },
  {
    heading: <>And the Roadmap keeps adjusting.</>,
    body: (
      <>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0 tw-mb-2">
          Every time a student completes an assessment, we update our understanding of what they know — not just that
          skill, but every connected skill. Then we use this to adjust their path. Automatically.
        </p>
        <p className="tw-font-atkinson lg:tw-text-2xl tw-text-xl tw-m-0 tw-mb-2">
          <span className="tw-font-bold">This happens for every student, every day.</span>
        </p>
        <p className="tw-font-atkinson lg:tw-text-xl tw-text-lg tw-m-0">
          Over a decade of research, working quietly in the background.
        </p>
      </>
    ),
    lottiePath: '/teacher-welcome-3-animation.json',
    classNames: 'paper-grid-bg-teacher-welcome-beige',
  },
];

// Root of each slide: orchestrates the two halves (text column + media panel)
// and re-runs every time `index` changes thanks to the keyed AnimatePresence.
const slideVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.15 },
  },
  exit: { opacity: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
};

// Text column slides in from the left and staggers its own pieces.
const textColVariants: Variants = {
  hidden: { opacity: 0, x: -24 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: 'easeOut', staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

// Each piece of the text column (dots row, copy, buttons) assembles in turn.
const textItemVariants: Variants = {
  hidden: { opacity: 0, x: -16 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

// The media panel enters from the right as a single block.
const panelVariants: Variants = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

interface Props {
  onSkip: () => void;
  onBack: () => void;
  onComplete: () => void;
}

const TeacherWelcomeWizard: FC<Props> = ({ onSkip, onBack, onComplete }) => {
  const [index, setIndex] = useState(0);
  const slide = SLIDES[index];

  const isLast = index === SLIDES.length - 1;

  const goNext = () => {
    if (isLast) {
      onComplete();
      return;
    }
    setIndex(current => current + 1);
  };

  const goBack = () => {
    if (index === 0) {
      onBack();
      return;
    }
    setIndex(current => current - 1);
  };

  return (
    <div className="tw--mt-5 tw--mb-[calc(1.25rem_+_75px)] tw-overflow-hidden lg:tw-h-[calc(100vh_-_90px)]">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          variants={slideVariants}
          initial="hidden"
          animate="show"
          exit="exit"
          className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[45%_55%] tw-h-full tw-items-stretch"
        >
          <motion.div
            variants={textColVariants}
            className="tw-flex tw-flex-col tw-gap-y-6 tw-px-20 tw-py-20 tw-justify-between tw-bg-white tw-h-[100%] tw-overflow-y-auto"
          >
            <motion.div variants={textItemVariants} className="tw-flex tw-items-center tw-justify-between">
              <div
                className="tw-flex tw-items-center tw-gap-x-2"
                role="progressbar"
                aria-label={`Step ${index + 1} of ${SLIDES.length}`}
                aria-valuenow={index + 1}
                aria-valuemin={1}
                aria-valuemax={SLIDES.length}
              >
                {SLIDES.map((_, dotIndex) => (
                  <span
                    key={dotIndex}
                    className={`tw-h-2 tw-rounded-full tw-transition-all tw-duration-300 ${
                      dotIndex === index ? 'tw-w-6 tw-bg-indigo-300' : 'tw-w-2 tw-bg-neutral-100'
                    }`}
                  />
                ))}
              </div>
              <Button
                className="tw-flex tw-flex-row-reverse tw-gap-x-2 !tw-text-neutral-800"
                label="Skip intro"
                icon={<ArrowRight size={16} className="tw-text-neutral-800 hover:tw-text-white" />}
                link
                onClick={onSkip}
              />
            </motion.div>
            <motion.div variants={textItemVariants} className="tw-flex tw-flex-col tw-gap-y-4">
              <h1 className="tw-font-sora tw-text-black tw-font-bold lg:tw-text-3xl tw-text-2xl tw-m-0">
                {slide.heading}
              </h1>
              <div className="tw-text-black tw-flex tw-flex-col tw-gap-y-4">{slide.body}</div>
            </motion.div>
            <motion.div variants={textItemVariants} className="tw-flex tw-gap-x-4 tw-ml-auto tw-items-center">
              <Button
                className="tw-w-12 tw-rounded-full tw-h-12 tw-justify-center"
                icon={<ArrowLeft size={20} />}
                rounded
                outlined
                aria-label="Back"
                onClick={goBack}
              />
              <Button
                className={`${
                  !isLast ? 'tw-w-12' : 'tw-gap-x-2'
                } tw-h-12 tw-rounded-full tw-justify-center tw-flex tw-flex-row-reverse`}
                icon={<ArrowRight size={20} />}
                rounded
                label={isLast ? 'Next' : ''}
                aria-label={isLast ? 'Finish intro' : 'Next'}
                onClick={goNext}
              />
            </motion.div>
          </motion.div>

          <motion.div
            variants={panelVariants}
            className={`tw-overflow-hidden ${slide.classNames} tw-flex tw-items-center tw-justify-center tw-h-[100%]`}
          >
            <div className="tw-h-[80%] tw-object-cover">
              <DotLottieReact src={slide.lottiePath} autoplay loop />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default TeacherWelcomeWizard;
